import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Alert, Paper, CircularProgress, Divider } from '@mui/material';
import { useFirebase } from '../../context/FirebaseContext';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Logo } from '../layout';
import { isDevelopment } from '../../config/env';

export default function EmailVerification() {
  const { auth, logOut, resendVerificationEmail } = useFirebase();
  const currentUser = useSelector((state) => state.user.currentUser);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();

  // Check if user is verified on reload
  useEffect(() => {
    const checkVerification = async () => {
      if (auth.currentUser) {
        await auth.currentUser.reload();
        if (auth.currentUser.emailVerified) {
          navigate('/');
        }
      }
    };

    const interval = setInterval(checkVerification, 10000); // Check every 10 seconds
    checkVerification();

    return () => clearInterval(interval);
  }, [auth, navigate]);

  // Countdown timer for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleResendEmail = async () => {
    try {
      setLoading(true);
      await resendVerificationEmail();
      setMessage('Verification email sent! Please check your inbox.');
      setError('');
      setCountdown(60); // 60 second cooldown
    } catch (err) {
      setError('Failed to resend verification email. Please try again later.');
      setMessage('');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setLoading(true);
      if (auth.currentUser) {
        await auth.currentUser.reload();
        if (auth.currentUser.emailVerified) {
          navigate('/');
        } else {
          setMessage('Email still not verified. Please check your inbox and spam folder.');
        }
      }
    } catch (err) {
      setError('Failed to check verification status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh',
        padding: 3,
        width: '100%',
        backgroundColor: 'background.default'
      }}
    >
      <Paper 
        elevation={3} 
        sx={{
          p: 4,
          maxWidth: 600,
          width: '100%',
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Logo sx={{ mb: 3 }} />
        
        <Typography variant="h4" component="h1" gutterBottom>
          Email Verification Required
        </Typography>
        
        <Typography variant="body1" textAlign="center" sx={{ mb: 3 }}>
          Please verify your email address <strong>{currentUser?.email}</strong> to continue.
        </Typography>
        
        <Box sx={{ width: '100%', mb: 3 }}>
          {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          <Alert severity="info" sx={{ mb: 2 }}>
            We've sent a verification link to your email. Please check your inbox and spam folder.
          </Alert>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, width: '100%', mb: 3 }}>
          <Button 
            variant="contained" 
            fullWidth 
            onClick={handleResendEmail} 
            disabled={loading || countdown > 0}
          >
            {loading ? <CircularProgress size={24} /> : 
              countdown > 0 ? `Resend in ${countdown}s` : "Resend Verification Email"}
          </Button>
          
          <Button 
            variant="outlined" 
            fullWidth 
            onClick={handleRefresh}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "I've Verified"}
          </Button>
        </Box>
        
        <Divider sx={{ width: '100%', mb: 3 }} />
        
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
          <Button onClick={logOut} color="secondary">
            Logout
          </Button>
          
          <Button 
            color="primary" 
            onClick={() => window.open('mailto:support@nanotradingcopilot.com')}
          >
            Contact Support
          </Button>
        </Box>
        
        {isDevelopment && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1, width: '100%' }}>
            <Typography variant="caption" color="text.secondary">
              Developer Mode: For testing, you can simulate email verification by refreshing the page.
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
} 